import React, { useEffect, useState } from "react";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact } from "./theme";
import { SkeletonCard, NoData, SourceChip, SectionTitle, DeltaBadge, BreakBadge } from "./components";
import { pulseGet } from "./api";

const MACRO_COLORS = ["#2f6f73", "#c8873a", "#8a5a9b", "#4f7fb3", "#b04e4e", "#9a9a9a"];

/** Eurostat arrivals by macro-region of residence (It.1 option b): stacked share bar for this country +
 *  a compact comparison of all catalogue countries. Accommodation arrivals, not border arrivals. */
export default function OriginMixSection({ iso3, dm }) {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    setLoading(true); setData(null);
    pulseGet("origin-mix").then((d) => { if (on) { setData(d); setLoading(false); } })
      .catch(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [iso3]);

  const me = data?.countries?.find((c) => c.ref_area === iso3);
  const lab = (m) => (lang === "it" ? m.label_it : m.label);
  const countryName = (c) => {
    if (!c.iso2) return c.ref_area_label;
    try { return new Intl.DisplayNames([lang], { type: "region" }).of(c.iso2) || c.ref_area_label; } catch { return c.ref_area_label; }
  };

  return (
    <section className="mb-4" data-testid="origin-mix">
      <SectionTitle dm={dm}>{t("origin_title")}</SectionTitle>
      {loading && <SkeletonCard h={200} dm={dm} />}
      {!loading && !me && <NoData dm={dm} h={120} msg={t("origin_no_data")} testid="origin-mix-no-data" />}
      {!loading && me && (
        <div className="p-4" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
            <span className="text-sm" style={{ color: T.muted }}>
              {t("origin_foreign_share", { year: me.year })}{" "}
              <strong className="pulse-num" style={{ color: T.text }} data-testid="origin-foreign-share">{me.foreign_share_pct}%</strong>
            </span>
            <DeltaBadge pct={me.foreign_yoy_pct} dm={dm} suffix=" " />
            {me.break_affected && <BreakBadge brk={me.series_break} dm={dm} short testid="origin-break-badge" />}
          </div>
          <div className="flex w-full overflow-hidden" style={{ height: 22, borderRadius: 2 }} role="img"
            aria-label={me.macro.map((m) => `${lab(m)} ${m.share_pct}%`).join(", ")} data-testid="origin-stacked-bar">
            {me.macro.map((m, i) => (
              <div key={m.code} title={`${lab(m)} · ${m.share_pct}%`}
                style={{ width: `${m.share_pct}%`, background: MACRO_COLORS[i % MACRO_COLORS.length], transition: "width .4s" }} />
            ))}
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5 mt-3 text-xs" data-testid="origin-legend">
            {me.macro.map((m, i) => (
              <li key={m.code} className="flex items-center gap-2">
                <span style={{ width: 8, height: 8, background: MACRO_COLORS[i % MACRO_COLORS.length], display: "inline-block" }} aria-hidden="true" />
                <span style={{ color: T.muted }}>{lab(m)}</span>
                <span className="pulse-num ml-auto font-semibold whitespace-nowrap">{m.share_pct}%</span>
                <span className="pulse-num text-[10px] whitespace-nowrap" style={{ color: T.muted }}>{fmtCompact(m.value, lang)}</span>
                <DeltaBadge pct={m.yoy_pct} dm={dm} suffix=" " />
              </li>
            ))}
          </ul>
          {data.countries.length > 1 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-xs" data-testid="origin-compare-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider" style={{ color: T.muted, borderBottom: `2px solid ${T.border}` }}>
                    <th className="py-1.5 font-bold" scope="col">{t("origin_col_country")}</th>
                    <th className="py-1.5 text-right font-bold" scope="col">{t("origin_col_foreign")}</th>
                    {me.macro.slice(0, 4).map((m) => <th key={m.code} className="py-1.5 text-right font-bold" scope="col">{lab(m)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.countries.map((c) => (
                    <tr key={c.ref_area} style={{ borderTop: `1px solid ${T.border}`, fontWeight: c.ref_area === iso3 ? 700 : 400 }}
                      data-testid={`origin-row-${c.ref_area}`}>
                      <td className="py-1.5 whitespace-nowrap">{countryName(c)} <span style={{ color: T.muted }}>{c.year}</span></td>
                      <td className="py-1.5 text-right pulse-num">{c.foreign_share_pct}%</td>
                      {me.macro.slice(0, 4).map((m) => {
                        const v = c.macro.find((x) => x.code === m.code);
                        return <td key={m.code} className="py-1.5 text-right pulse-num">{v ? `${v.share_pct}%` : "—"}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] mt-3" style={{ color: T.muted }} data-testid="origin-note">{lang === "it" ? data.note_it : data.note}</p>
          <div className="mt-1"><SourceChip source={data.source_org} dataset={data.dataset_code} period={String(me.year)} dm={dm} /></div>
        </div>
      )}
    </section>
  );
}
